// src/api/apiBasePortal.js
/**
 * Portal API base with Auth0/bearer token support.
 * Returns a consistent shape: { ok, status, data, error, requestId }.
 * Exposes a named `api` object and helpers used by the Club Users UI.
 *
 * Exports `setTokenGetter` for TokenBridge.
 */

import { resolveRegisteredAccessToken, setRegisteredAccessTokenGetter } from '@/utils/registeredAccessToken.js';

let tokenGetter = null;

const isProdEnv = (() => {
  if (typeof import.meta !== "undefined" && import.meta.env && import.meta.env.MODE) {
    return import.meta.env.MODE === "production";
  }
  return typeof process !== "undefined" && process.env?.NODE_ENV === "production";
})();

export function shouldLogApiDebug() {
  if (!isProdEnv) return true;
  try {
    return Boolean(window?.__DEBUG_API__);
  } catch {
    return false;
  }
}

/** Allow UI code to provide a bearer token resolver. */
export function setTokenGetter(fn) {
  tokenGetter = typeof fn === "function" ? fn : null;
  setRegisteredAccessTokenGetter(tokenGetter);
}

/** Resolve the API base from env (works on Vite + Vercel) */
const BASE =
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_API_BASE) ||
  (typeof process !== "undefined" && process.env?.VITE_API_BASE) ||
  "";

const API_BASE = `${BASE}`.replace(/\/+$/, "");

/** Convenience: get the current Auth0 user if TokenBridge set it */
export function getCurrentUser() {
  try {
    return typeof window !== "undefined" ? window.auth0User || null : null;
  } catch {
    return null;
  }
}

function extractClubCodeFromPath(pathname) {
  if (!pathname || typeof pathname !== "string") return null;
  const match = pathname.match(/^\/(\d{6})(?:\b|\/)/);
  return match ? match[1] : null;
}

function resolveClubCodeFromLocation() {
  if (typeof window === "undefined") return null;
  try {
    return extractClubCodeFromPath(window.location?.pathname || "");
  } catch {
    return null;
  }
}

function hasHeader(headers, name) {
  if (!headers) return false;
  if (typeof headers.get === "function") {
    return Boolean(headers.get(name));
  }
  return Object.keys(headers).some((key) => key.toLowerCase() === name.toLowerCase());
}

function getHeaderValue(headers, name) {
  if (!headers) return null;
  if (typeof headers.get === "function") {
    return headers.get(name);
  }
  const key = Object.keys(headers).find((h) => h.toLowerCase() === name.toLowerCase());
  return key ? headers[key] : null;
}

function isRawTextContentType(contentType) {
  if (!contentType || typeof contentType !== "string") return false;
  const normalized = contentType.split(";")[0].trim().toLowerCase();
  return normalized === "text/csv" || normalized === "application/csv" || normalized === "text/plain";
}

/** Get a bearer token if possible (TokenBridge, Auth0, or custom) */
async function getBearer(endpoint = "") {
  if (tokenGetter) {
    try {
      const v = await tokenGetter();
      if (v) return v;
    } catch {}
  }
  // TokenBridge also publishes its getter in the shared registry.  Consulting
  // it here closes the bootstrap/module-instance gap while retaining the one
  // canonical Auth0 token source (audience, scope and organisation included).
  try {
    const v = await resolveRegisteredAccessToken();
    if (v) return v;
  } catch {}
  try {
    const tb = globalThis?.TokenBridge;
    if (tb?.getAccessTokenSilently) {
      const v = await tb.getAccessTokenSilently();
      if (v) return v;
    }
  } catch {}
  try {
    const f = globalThis?.getAccessTokenSilently;
    if (typeof f === "function") {
      const v = await f();
      if (v) return v;
    }
  } catch {}
  if (shouldLogApiDebug()) {
    console.warn("[apiBasePortal] missing bearer token for", endpoint);
  }
  return null;
}

/** Core request wrapper */
async function request(
  method,
  path,
  body,
  { headers = {}, timeout, credentials, signal } = {}
) {
  const ctrl = timeout ? new AbortController() : null;
  const to = timeout ? setTimeout(() => ctrl.abort("timeout"), timeout) : null;
  const combinedSignal =
    ctrl && signal && typeof AbortSignal !== "undefined" && AbortSignal.any
      ? AbortSignal.any([ctrl.signal, signal])
      : ctrl?.signal || signal;

  const isFormData =
    typeof FormData !== "undefined" &&
    (body instanceof FormData || body?.[Symbol.toStringTag] === "FormData");

  try {
    const token = await getBearer(path);
    const clubCode = resolveClubCodeFromLocation();
    const url =
      path.startsWith("http://") || path.startsWith("https://")
        ? path
        : `${API_BASE}${path}`;

    const defaultHeaders = {
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(!hasHeader(headers, "x-club-code") && clubCode
        ? { "x-club-code": clubCode }
        : {}),
      ...(!isFormData && body != null ? { "Content-Type": "application/json" } : {}),
    };

    const mergedHeaders = { ...defaultHeaders, ...headers };
    const requestContentType = getHeaderValue(mergedHeaders, "content-type");
    const shouldPassRawText =
      typeof body === "string" &&
      !isFormData &&
      isRawTextContentType(requestContentType);
    const requestBody =
      body == null
        ? undefined
        : isFormData
          ? body
          : shouldPassRawText
            ? body
            : JSON.stringify(body);

    const opts = {
      method,
      headers: mergedHeaders,
      ...(body != null ? { body: requestBody } : {}),
      ...(combinedSignal ? { signal: combinedSignal } : {}),
      ...(credentials ? { credentials } : {}),
    };

    const res = await fetch(url, opts);
    const requestId = res.headers.get("x-request-id") || null;
    const status = res.status;
    const responseContentType = res.headers.get("content-type") || "";
    const text = await res.text().catch(() => "");
    let parsed = null;

    if (responseContentType.includes("application/json")) {
      try {
        parsed = text ? JSON.parse(text) : null;
      } catch (parseError) {
        const envelope = {
          ok: false,
          status,
          data: null,
          error: {
            code: "invalid_json",
            message: parseError?.message || "Response was not valid JSON",
          },
          requestId,
        };
        if (shouldLogApiDebug()) {
          console.error("[api:error]", {
            requestId,
            method,
            url,
            status,
            error: envelope.error,
          });
        }
        return envelope;
      }
    } else {
      const envelope = {
        ok: false,
        status,
        data: null,
        error: {
          code: "invalid_json",
          message: `Expected JSON but received ${responseContentType || "unknown content type"}`,
        },
        requestId,
      };
      if (shouldLogApiDebug()) {
        console.error("[api:error]", {
          requestId,
          method,
          url,
          status,
          error: envelope.error,
        });
      }
      return envelope;
    }

    const isObjectPayload = parsed && typeof parsed === "object";
    const hasOwn = (key) => isObjectPayload && Object.prototype.hasOwnProperty.call(parsed, key);
    const isEnvelope = hasOwn("ok") && hasOwn("data") && hasOwn("error");
    const looksLikeStructuredErrorBody = isObjectPayload && (hasOwn("ok") || hasOwn("error") || hasOwn("data"));

    let envelope = null;
    if (isEnvelope) {
      envelope = {
        ok: Boolean(parsed.ok),
        status,
        data: parsed.data ?? null,
        error: parsed.error ?? null,
        requestId: parsed.requestId || requestId,
      };
    } else if (res.ok) {
      envelope = {
        ok: true,
        status,
        data: parsed ?? null,
        error: null,
        requestId,
      };
    } else if (looksLikeStructuredErrorBody) {
      envelope = {
        ok: false,
        status,
        data: hasOwn("data") ? parsed.data ?? null : null,
        error: hasOwn("error") ? parsed.error ?? null : { code: `http_${res.status}`, message: `HTTP ${res.status}` },
        requestId: parsed?.requestId || requestId,
      };
    } else {
      const message = parsed?.message || parsed?.error || `HTTP ${res.status}`;
      const code = parsed?.code || parsed?.error || `http_${res.status}`;
      envelope = {
        ok: false,
        status,
        data: parsed ?? null,
        error: { code: String(code), message: String(message) },
        requestId,
      };
    }

    if (parsed?.details) envelope.details = parsed.details;

    if (shouldLogApiDebug() && !envelope.ok) {
      console.error("[api:error]", {
        requestId: envelope.requestId,
        method,
        url,
        status,
        error: envelope.error,
      });
    }

    return envelope;
  } catch (e) {
    const isAbort = e?.name === "AbortError";
    const status = isAbort ? 408 : 500;
    const envelope = {
      ok: false,
      status,
      data: null,
      error: {
        code: isAbort ? "timeout" : "network_error",
        message: e?.message || (isAbort ? "Request timeout" : "Request failed"),
      },
      requestId: null,
    };
    if (shouldLogApiDebug()) {
      console.error("[api:error]", {
        requestId: envelope.requestId,
        method,
        url: path,
        status,
        error: envelope.error,
      });
    }
    return envelope;
  } finally {
    if (to) clearTimeout(to);
  }
}

/** HTTP verbs */
function get(path, opts) { return request("GET", path, null, opts); }
function post(path, body, options = {}) {
  const { extraHeaders, headers = {}, ...rest } = options || {};
  const mergedHeaders = {
    ...(extraHeaders || {}),
    ...headers,
  };
  return request("POST", path, body, { ...rest, headers: mergedHeaders });
}
function put(path, body, opts) { return request("PUT", path, body, opts); }
function patch(path, body, opts) { return request("PATCH", path, body, opts); }
function del(path, opts) { return request("DELETE", path, null, opts); }

async function postBlob(path, body, { headers = {}, timeout, credentials = "include", signal } = {}) {
  const ctrl = timeout ? new AbortController() : null;
  const to = timeout ? setTimeout(() => ctrl.abort("timeout"), timeout) : null;
  const combinedSignal = ctrl && signal && typeof AbortSignal !== "undefined" && AbortSignal.any ? AbortSignal.any([ctrl.signal, signal]) : ctrl?.signal || signal;
  try {
    const token = await getBearer(path);
    const clubCode = resolveClubCodeFromLocation();
    const url = path.startsWith("http://") || path.startsWith("https://") ? path : `${API_BASE}${path}`;
    const mergedHeaders = {
      Accept: "application/pdf",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(!hasHeader(headers, "x-club-code") && clubCode ? { "x-club-code": clubCode } : {}),
      "Content-Type": "application/json",
      ...headers,
    };
    return fetch(url, { method: "POST", headers: mergedHeaders, body: body == null ? undefined : JSON.stringify(body), ...(combinedSignal ? { signal: combinedSignal } : {}), ...(credentials ? { credentials } : {}) });
  } finally {
    if (to) clearTimeout(to);
  }
}

async function getBlob(
  path,
  { headers = {}, timeout, credentials = "include", signal } = {}
) {
  const ctrl = timeout ? new AbortController() : null;
  const to = timeout ? setTimeout(() => ctrl.abort("timeout"), timeout) : null;
  const combinedSignal =
    ctrl && signal && typeof AbortSignal !== "undefined" && AbortSignal.any
      ? AbortSignal.any([ctrl.signal, signal])
      : ctrl?.signal || signal;
  try {
    const token = await getBearer(path);
    const clubCode = resolveClubCodeFromLocation();
    const url =
      path.startsWith("http://") || path.startsWith("https://")
        ? path
        : `${API_BASE}${path}`;
    const mergedHeaders = {
      Accept: "application/octet-stream",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(!hasHeader(headers, "x-club-code") && clubCode ? { "x-club-code": clubCode } : {}),
      ...headers,
    };
    const response = await fetch(url, {
      method: "GET",
      headers: mergedHeaders,
      ...(combinedSignal ? { signal: combinedSignal } : {}),
      ...(credentials ? { credentials } : {}),
    });
    return response;
  } finally {
    if (to) clearTimeout(to);
  }
}


export function asArray(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.data?.items)) return value.data.items;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.results)) return value.results;
  if (Array.isArray(value?.rows)) return value.rows;
  return [];
}

/** Named helpers kept for compatibility across the app */
export const apiGet = get;
// Interactive OAuth starts need the Portal bearer identity and browser cookie
// transport. Keep this opt-in: ordinary API calls do not need ambient cookies.
export const apiStartMicrosoftOAuth = (path) => get(path, { credentials: "include" });
export const apiGetBlob = getBlob;
export const apiPostBlob = postBlob;
export const apiPost = post;
export const apiPut = put;
export const apiPatch = patch;
export const apiDelete = del;

/* ====== Club helpers (unchanged) ====== */
async function listClubUsers(clubRef) {
  return get(`/api/admin/clubs/${encodeURIComponent(clubRef)}/users`, { timeout: 15000 });
}
async function createClubUser(clubRef, payload) {
  return post(`/api/admin/clubs/${encodeURIComponent(clubRef)}/users`, payload, { timeout: 15000 });
}
async function deleteClubUser(clubRef, userId) {
  return del(`/api/admin/clubs/${encodeURIComponent(clubRef)}/users/${encodeURIComponent(userId)}`, { timeout: 15000 });
}
async function sendWelcomeEmail(userId) {
  return post(`/api/admin/users/${encodeURIComponent(userId)}/welcome`, {}, { timeout: 15000 });
}

/** Named export so `import { api } from "./apiClient"` works */
export const api = {
  base: API_BASE,
  get, post, postBlob, put, patch, delete: del,
  listClubUsers, createClubUser, deleteClubUser, sendWelcomeEmail,
  setTokenGetter, // expose for callers that import api.setTokenGetter
  getCurrentUser,
};

/** Default export for legacy imports */
export default api;

if (typeof import.meta !== "undefined" && import.meta.env?.DEV) {
  const host = typeof window !== "undefined" ? window.location.host : "server";
  console.debug("[apiBasePortal] loaded on host", host);
}
